<script setup lang="ts">
// no direct ref imports needed
import { Button } from '@/components/ui/button'
import { useRouter } from 'vue-router'
import { useForm11 } from '@/composables/useForm11'
import MainLayout from '@/components/layout/MainLayout.vue'
import { usePensionCalculations } from '@/composables/usePensionCalculations'
import { useAuth } from '@/composables/useAuth'
import { faker } from '@faker-js/faker/locale/en'
import { useQuestionState } from '@/composables/useQuestionState'

interface IncomeType {
  id: string
  label: string
}

const incomeTypes: IncomeType[] = [
  { id: 'canadian-dividends', label: 'Canadian dividends' },
  { id: 'dividends-distributions', label: 'Dividends & distributions' },
  { id: 'emp-off-pen', label: 'Emp / Off / Pen not subject to PAYE' },
  { id: 'fees-commissions', label: 'Fees and commissions' },
  { id: 'foreign-pensions', label: 'Foreign pensions' },
  { id: 'foreign-salary', label: 'Foreign salary' },
  { id: 'maintenance-payments', label: 'Maintenance payments received' },
  { id: 'rental-income', label: 'Rental income' },
  { id: 'trading-profit', label: 'Trading profit or professional income' },
  { id: 'uk-dividends', label: 'UK dividends' },
  { id: 'us-dividends', label: 'US dividends' },
  { id: 'untaxed-state-income', label: 'Untaxed income arising in the State' },
  { id: 'irish-taxed-income', label: 'Irish taxed income' },
]

const router = useRouter()
const form11 = useForm11()
const { taxback } = usePensionCalculations()
const { refreshProfile } = useAuth()
const state = useQuestionState('IncomeSelection', {
  selectedIncomes: [] as string[],
})
const { selectedIncomes } = state

const handleCancel = () => {
  router.back()
}

const fillMockData = () => {
  selectedIncomes = faker.helpers.arrayElements(
    incomeTypes.map((type) => type.id),
    faker.number.int({ min: 1, max: 5 }),
  )
}

function toggleSelection(incomeId: string) {
  if (selectedIncomes.includes(incomeId)) {
    selectedIncomes = selectedIncomes.filter(
      (id) => id !== incomeId,
    )
  } else {
    selectedIncomes.push(incomeId)
  }
}

function isSelected(incomeId: string) {
  return selectedIncomes.includes(incomeId)
}

async function continueToForms() {
  // Save the selected incomes to the form11 composable
  form11.saveSelectedIncomes(selectedIncomes)

  if (selectedIncomes.length > 0) {
    router.push('/form11/final-form')
  }
  // If nothing is selected at all
  else {
    await refreshProfile()
    alert(
      `Congratulations! Based on the information you provided, you may be due a tax refund of €${taxback.value.toFixed(2)}.`,
    )
    router.push('/tax-refund-options')
  }
}
</script>

<template>
  <MainLayout>
    <div class="max-w-5xl mx-auto p-6">
      <div class="mb-6">
        <h1 class="text-2xl font-bold mb-2">Declare additional incomes</h1>
        <p class="text-gray-600 mb-4">
          Additional incomes are incomes which are not taxed through the PAYE
          system such as rental income or certain DSP payments.If you are in
          receipt of any additional incomes you must tell us about them so that
          any tax due can be collected through the PAYE system. This is done by
          reducing your tax credits. If you are Income Tax registered (i.e. you
          submit an annual Form 11 tax return each year) you should only declare
          your DSP incomes below. Your annual tax return should include details
          of all sources of income, including any DSP payments received.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div
          v-for="income in incomeTypes"
          :key="income.id"
          class="border rounded-lg flex flex-col text-center gap-2 justify-center items-center cursor-pointer h-20"
          :class="isSelected(income.id) ? 'bg-primary text-white' : ''"
          @click="toggleSelection(income.id)"
        >
          <span>{{ income.label }}</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4">
        <Button variant="secondary" @click="handleCancel" class="flex-1"
          >Cancel</Button
        >
        <div class="flex-1"></div>
        <Button @click="continueToForms" class="flex-1">Submit</Button>
      </div>

      <!-- Mock Data Button (Development Only) -->
      <Button @click="fillMockData" variant="outline" class="w-full">
        Fill Mock Data
      </Button>
    </div>
  </MainLayout>
</template>

<style scoped>
.select-button {
  min-width: 100px;
}
</style>
